import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';

/**
 * Bloom/Flash Effect Manager
 * 
 * Creates a bloom filter effect that can be triggered in sync with audio events.
 * Uses GSAP for smooth intensity animations.
 */
export class BloomEffect {
    private bloomFilter: PIXI.Filter & { strength?: number; blur?: number };
    private currentTween: gsap.core.Timeline | null = null;

    constructor() {
        // Use a simple blur filter as bloom effect
        try {
            const blurFilter = new PIXI.BlurFilter();
            (blurFilter as any).strength = 0;
            this.bloomFilter = blurFilter as any;
        } catch (error) {
            console.error('[BloomEffect] Error creating filter:', error);
            this.bloomFilter = new PIXI.Filter();
        }
    }

    /**
     * Get the PixiJS filter instance
     * Apply this to your sprite/container
     */
    public getFilter(): PIXI.Filter {
        return this.bloomFilter;
    }

    /**
     * Trigger a bloom flash effect
     * 
     * @param intensity - Peak bloom intensity (0-10+, typical: 2-5)
     * @param duration - Duration of the flash in seconds
     */
    public trigger(intensity: number = 3.0, duration: number = 0.15): void {
        // Kill any existing animation
        if (this.currentTween) {
            this.currentTween.kill();
        }

        // Animate bloom intensity: 0 -> peak -> 0
        this.currentTween = gsap.timeline()
            .to(this.bloomFilter, {
                strength: intensity,
                duration: duration * 0.3, // Quick ramp up (30% of duration)
                ease: 'power2.out',
            })
            .to(this.bloomFilter, {
                strength: 0,
                duration: duration * 0.7, // Slower fade out (70% of duration)
                ease: 'power2.in',
            });
    }

    /**
     * Manually set bloom intensity
     * Useful for testing or custom effects
     * 
     * @param intensity - Bloom strength (0-10+)
     */
    public setIntensity(intensity: number): void {
        if (this.currentTween) {
            this.currentTween.kill();
        }
        this.bloomFilter.strength = intensity;
    }

    /**
     * Reset bloom to default state
     */
    public reset(): void {
        if (this.currentTween) {
            this.currentTween.kill();
        }
        this.bloomFilter.strength = 0;
    }

    /**
     * Clean up resources
     */
    public dispose(): void {
        if (this.currentTween) {
            this.currentTween.kill();
        }
        this.bloomFilter.destroy();
    }
}
